// Timeline constants
export const TIMELINE_START_HOUR = 0; // 12 AM
export const TIMELINE_END_HOUR = 24; // 12 AM next day
export const CORE_START_HOUR = 8.5; // 8:30 AM (core hours)
export const CORE_END_HOUR = 17.5; // 5:30 PM (core hours)
export const PX_PER_HOUR = 42; // pixels per hour in timeline (24h fits ~1008px)
export const DEFAULT_DURATION = 30; // default task duration in minutes
export const SNAP_INTERVAL = 5; // snap to nearest 5 minutes
export const MIN_PLANNER_PX_PER_HOUR = 96; // 15 min blocks render at 24px
export const ADAPTIVE_TIMELINE_PADDING_MINUTES = 30;
export const MINUTES_PER_DAY = 24 * 60;
export const TIMELINE_GUTTER_WIDTH = 48;
export const TIMELINE_CONTENT_RIGHT_PAD = 4;

// Total height of the timeline in pixels
export const TIMELINE_HEIGHT =
  (TIMELINE_END_HOUR - TIMELINE_START_HOUR) * PX_PER_HOUR;

/**
 * Convert "HH:MM" string to minutes since midnight.
 * Returns 0 for invalid formats.
 */
export function timeToMinutes(time: string): number {
  if (!time || typeof time !== "string") return 0;
  const match = time.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return 0;
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (isNaN(hours) || isNaN(minutes)) return 0;
  if (minutes < 0 || minutes > 59) return 0;
  return hours * 60 + minutes;
}

/**
 * Convert minutes since midnight to "HH:MM" string (24h format).
 * Clamps to 0-1439.
 */
export function minutesToTime(minutes: number): string {
  const clamped = Math.max(0, Math.min(1439, Math.round(minutes)));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/**
 * Convert minutes since midnight to pixel offset from top of timeline.
 */
export function minutesToOffset(minutes: number): number {
  return ((minutes - TIMELINE_START_HOUR * 60) * PX_PER_HOUR) / 60;
}

/**
 * Convert pixel offset from top of timeline to minutes since midnight.
 */
export function offsetToMinutes(px: number): number {
  return (px * 60) / PX_PER_HOUR + TIMELINE_START_HOUR * 60;
}

/**
 * Snap minutes to nearest interval (default 5 min).
 */
export function snapToGrid(
  minutes: number,
  interval: number = SNAP_INTERVAL,
): number {
  return Math.round(minutes / interval) * interval;
}

/**
 * Format minutes since midnight to display label: "9:00 AM", "2:30 PM".
 */
export function formatTimeLabel(minutes: number): string {
  const totalMinutes = Math.max(0, Math.min(1439, Math.round(minutes)));
  let hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  const period = hours >= 12 ? "PM" : "AM";

  if (hours === 0) {
    hours = 12;
  } else if (hours > 12) {
    hours -= 12;
  }

  return `${hours}:${String(mins).padStart(2, "0")} ${period}`;
}

/**
 * Get the pixel height for a given duration in minutes.
 */
export function durationToHeight(durationMinutes: number): number {
  return (durationMinutes * PX_PER_HOUR) / 60;
}

/**
 * Height for a scheduled block at the current grid density.
 *
 * Do not enforce a large visual minimum here: adjacent short blocks can be
 * scheduled back-to-back, and a minimum larger than their actual duration
 * makes them appear to overlap even when their times do not.
 */
export function getTimelineBlockHeight(
  durationMinutes: number,
  pxPerHour: number,
): number {
  if (!Number.isFinite(durationMinutes) || !Number.isFinite(pxPerHour)) return 1;
  return Math.max((durationMinutes * pxPerHour) / 60, 1);
}

export function getPlannerPxPerHour(
  availableHeight: number,
  visibleHours: number,
): number {
  if (!Number.isFinite(availableHeight) || !Number.isFinite(visibleHours) || visibleHours <= 0) {
    return MIN_PLANNER_PX_PER_HOUR;
  }
  return Math.max(availableHeight / visibleHours, MIN_PLANNER_PX_PER_HOUR);
}

export interface AdaptiveTimelineRange {
  startHour: number;
  endHour: number;
  startMin: number;
  endMin: number;
}

export interface TimelineRangeItem {
  startMin: number;
  endMin: number;
}

export interface NowIndicatorPosition {
  minutes: number;
  top: number;
}

export type TimelineBlockVisualKind = 'task' | 'project' | 'subtask';

export function getTimelineBlockVisualKind(block: {
  itemType?: string | null;
  parentId?: string | null;
}): TimelineBlockVisualKind {
  if (block.parentId) return 'subtask';
  if (block.itemType === 'project') return 'project';
  return 'task';
}

export function canScheduleTimelineItem(_item: { parentId?: string | null }): boolean {
  return true;
}

function clampDayMinute(minutes: number): number {
  return Math.max(0, Math.min(MINUTES_PER_DAY, minutes));
}

/**
 * Computes the visible Track timeline window.
 *
 * The default is the normal workday. It only expands when scheduled content
 * falls outside that range, which keeps empty after-hours space from growing
 * while preserving early/late commitments when they exist.
 */
export function computeAdaptiveTimelineRange(
  items: TimelineRangeItem[],
  options?: {
    workdayStartMin?: number;
    workdayEndMin?: number;
    paddingMinutes?: number;
  },
): AdaptiveTimelineRange {
  const workdayStartMin = options?.workdayStartMin ?? CORE_START_HOUR * 60;
  const workdayEndMin = options?.workdayEndMin ?? CORE_END_HOUR * 60;
  const paddingMinutes = options?.paddingMinutes ?? ADAPTIVE_TIMELINE_PADDING_MINUTES;

  let startMin = workdayStartMin;
  let endMin = workdayEndMin;

  for (const item of items) {
    if (
      !Number.isFinite(item.startMin) ||
      !Number.isFinite(item.endMin) ||
      item.endMin <= item.startMin
    ) {
      continue;
    }

    if (item.startMin < workdayStartMin) {
      startMin = Math.min(startMin, Math.floor((item.startMin - paddingMinutes) / 60) * 60);
    }
    if (item.endMin > workdayEndMin) {
      endMin = Math.max(endMin, Math.ceil((item.endMin + paddingMinutes) / 60) * 60);
    }
  }

  startMin = clampDayMinute(startMin);
  endMin = clampDayMinute(endMin);

  if (endMin <= startMin) {
    startMin = workdayStartMin;
    endMin = workdayEndMin;
  }

  return {
    startHour: startMin / 60,
    endHour: endMin / 60,
    startMin,
    endMin,
  };
}

export function getNowIndicatorPosition(
  now: Date,
  startHour: number,
  endHour: number,
  pxPerHour: number,
): NowIndicatorPosition | null {
  const minutes = now.getHours() * 60 + now.getMinutes();
  const startMin = startHour * 60;
  const endMin = endHour * 60;
  if (minutes < startMin || minutes > endMin) return null;

  return {
    minutes,
    top: ((minutes - startMin) * pxPerHour) / 60,
  };
}

/**
 * Get duration in minutes from a pixel height.
 */
export function heightToDuration(height: number): number {
  return (height * 60) / PX_PER_HOUR;
}

// ── Column layout for overlapping timeline items ──

export interface ColumnLayout {
  column: number;
  totalColumns: number;
}

/**
 * Compute non-overlapping column assignments for timeline items.
 * Groups overlapping items into clusters, then greedily assigns each item
 * to the first column whose previous occupant has already ended.
 */
export function computeColumnLayouts(items: Array<{ id: string; startMin: number; endMin: number }>): Map<string, ColumnLayout> {
  const result = new Map<string, ColumnLayout>();
  if (items.length === 0) return result;

  const sorted = [...items].sort((a, b) => a.startMin - b.startMin || a.endMin - b.endMin);

  // Group overlapping items into clusters
  const clusters: Array<typeof sorted> = [];
  let currentCluster = [sorted[0]];
  let clusterEnd = sorted[0].endMin;

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i].startMin < clusterEnd) {
      currentCluster.push(sorted[i]);
      clusterEnd = Math.max(clusterEnd, sorted[i].endMin);
    } else {
      clusters.push(currentCluster);
      currentCluster = [sorted[i]];
      clusterEnd = sorted[i].endMin;
    }
  }
  clusters.push(currentCluster);

  // Assign columns within each cluster
  for (const cluster of clusters) {
    const columns: number[][] = []; // columns[col] = array of endMin values
    for (const item of cluster) {
      let placed = false;
      for (let col = 0; col < columns.length; col++) {
        const lastEnd = columns[col][columns[col].length - 1];
        if (item.startMin >= lastEnd) {
          columns[col].push(item.endMin);
          result.set(item.id, { column: col, totalColumns: 0 });
          placed = true;
          break;
        }
      }
      if (!placed) {
        columns.push([item.endMin]);
        result.set(item.id, { column: columns.length - 1, totalColumns: 0 });
      }
    }
    const totalCols = columns.length;
    for (const item of cluster) {
      const layout = result.get(item.id)!;
      layout.totalColumns = totalCols;
    }
  }

  return result;
}

/**
 * Clamp duration to min/max bounds.
 */
export function clampDuration(
  minutes: number,
  min: number = 15,
  max: number = 240,
): number {
  return Math.max(min, Math.min(max, minutes));
}

/**
 * Generate array of hour marks for the grid.
 * From TIMELINE_START_HOUR to TIMELINE_END_HOUR inclusive.
 */
export function getHourMarks(): Array<{ minutes: number; label: string }> {
  const marks: Array<{ minutes: number; label: string }> = [];
  for (let hour = TIMELINE_START_HOUR; hour <= TIMELINE_END_HOUR; hour++) {
    const minutes = hour * 60;
    const effectiveHour = hour % 24;
    let displayHour = effectiveHour;
    const period = effectiveHour >= 12 && effectiveHour < 24 ? "PM" : "AM";

    if (displayHour === 0 || displayHour === 24) {
      displayHour = 12;
    } else if (displayHour > 12) {
      displayHour -= 12;
    }

    marks.push({ minutes, label: `${displayHour} ${period}` });
  }
  return marks;
}
